"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Feature grid (item 4): the old four-column text-only "proof strip"
 * (main.html's `.proof-strip`) made scroll-animated and visually rich —
 * an eyebrow/heading pair, an icon + colored tile per card, and a
 * staggered reveal — while staying strictly product-mechanics copy, no
 * invented stats (ui-ux-spec.md's "honest value strip" requirement).
 */

import { ShoppingCart, Sprout, UtensilsCrossed, Wallet } from "lucide-react"
import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"

const ICONS = [UtensilsCrossed, ShoppingCart, Sprout, Wallet]
const TILE_COLORS = [
  "bg-lime/30 text-green",
  "bg-sky/40 text-ink",
  "bg-coral/20 text-coral",
  "bg-gold/25 text-ink",
]

export function ValueStrip() {
  const { t } = useI18n()

  const items = [
    { title: t("landingPage.valueStrip.item1Title"), body: t("landingPage.valueStrip.item1Body") },
    { title: t("landingPage.valueStrip.item2Title"), body: t("landingPage.valueStrip.item2Body") },
    { title: t("landingPage.valueStrip.item3Title"), body: t("landingPage.valueStrip.item3Body") },
    { title: t("landingPage.valueStrip.item4Title"), body: t("landingPage.valueStrip.item4Body") },
  ]

  return (
    <section aria-labelledby="value-strip-heading" className="border-b border-line bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.valueStrip.eyebrow")}
          </p>
          <h2 id="value-strip-heading" className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {t("landingPage.valueStrip.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">{t("landingPage.valueStrip.lead")}</p>
        </Reveal>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 md:grid-cols-4">
          {items.map((item, i) => {
            const Icon = ICONS[i]
            return (
              <Reveal key={item.title} index={i + 1}>
                <article className="group flex h-full flex-col gap-4 rounded-bb-xl border border-line bg-cream p-6 transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-[var(--bb-shadow)]">
                  <span
                    className={`flex size-11 items-center justify-center rounded-bb-md ${TILE_COLORS[i]}`}
                  >
                    <Icon aria-hidden="true" className="size-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-ink">{item.title}</h3>
                    <p className="mt-1 text-sm text-ink-2">{item.body}</p>
                  </div>
                </article>
              </Reveal>
            )
          })}
        </div>
      </div>
    </section>
  )
}
